import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  ShoppingCart, MapPin, ChefHat, ChevronUp, ChevronDown, X, 
  Gift, CreditCard, CheckCircle, Package, MessageSquare, Wine,
  ScanLine, Loader2, Minus, Plus, Trash2
} from 'lucide-react';

/**
 * UnifiedCart - Single cart component for all devices
 * 
 * Responsive design:
 * - Mobile: Full-screen overlay when expanded
 * - Tablet: Side panel
 * - Desktop: Fixed sidebar
 * 
 * Same logic, same components, same behavior everywhere
 */
const UnifiedCart = ({
  // Cart data
  cart = [],
  orderItems = [],
  selectedTable = null,
  orderTotals = {},
  
  // Tables
  availableTables = [],
  loadingTables = false,
  
  // Actions
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onSendToKitchen,
  onRequestPayment,
  onAssignTable,
  onUnlinkTable,
  onFetchTables,
  onLoyaltyClick,
  onRewardScanClick,
  onMessageClick,
  onOpenSupplementDialog, // NEW: For opening supplement dialog
  
  // State
  canSendToKitchen = false,
  canScanLoyalty = false,
  selectedLoyaltyMember = null,
  isTakeaway = false,
  setIsTakeaway,
  
  // Mobile specific
  isExpanded = false,
  onToggleExpand,
  
  // Helpers
  getLoyaltyStatusColor,
  getLoyaltyStatusIcon,
}) => {
  // Local state
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const dropdownRef = useRef(null);
  
  // Calculate totals
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0) + 
                    orderItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalAmount = orderTotals?.orderTotal || cartTotal;
  const itemsToSend = cart.filter(item => !item.sent);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ne fermer que si le dropdown est ouvert et si on clique vraiment en dehors
      if (showTableDropdown && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowTableDropdown(false);
      }
    };
    // Utiliser setTimeout pour éviter les conflits avec le clic d'ouverture
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTableDropdown]);
  
  // ==================== CART ITEM COMPONENT ====================
  const CartItem = ({ item, index, isSent = false }) => {
    const status = item.kitchen_status || item.bar_status;
    const statusConfig = {
      'en_attente': { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', label: 'En attente' },
      'en_preparation': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'En préparation' },
      'pret': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'Prêt !' },
      'probleme': { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Problème' },
    };
    const config = statusConfig[status] || { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-700', label: '' };
    
    return (
      <div className={cn(
        "p-3 rounded-xl border-2 transition-all",
        isSent ? `${config.bg} ${config.border}` : "bg-white border-gray-100 shadow-sm",
        "dark:bg-zinc-800 dark:border-zinc-700"
      )}>
        <div className="flex items-start gap-3">
          {/* Item info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={cn(
                "font-bold text-base",
                item.isReward && "text-purple-700 dark:text-purple-400"
              )}>
                {item.product_name || item.name || 'Produit'}
              </p>
              {item.isReward && (
                <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                  <Gift className="w-3 h-3 mr-1" />
                  Fidélité
                </Badge>
              )}
              {isSent && config.label && (
                <Badge variant="outline" className={cn("text-xs", config.text)}>
                  {item.is_bar_item ? '🍹' : '🍳'} {config.label}
                </Badge>
              )}
            </div>
            
            {item.accompaniment && (
              <p className="text-sm text-orange-600 dark:text-orange-400 mt-0.5">
                + {item.accompaniment.name}
              </p>
            )}
            
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {(item.price || item.unit_price || 0).toLocaleString()} F × {item.quantity}
            </p>
            
            {/* Supplements */}
            {item.supplements?.length > 0 && (
              <div className="mt-2 pl-3 border-l-2 border-amber-400 space-y-0.5">
                {item.supplements.map((sup, si) => (
                  <div key={si} className="flex justify-between text-sm text-amber-700 dark:text-amber-400">
                    <span>+ {sup.name}</span>
                    <span>+{sup.price?.toLocaleString()} F</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Price and controls */}
          <div className="flex flex-col items-end gap-2">
            <p className="font-bold text-lg text-gray-900 dark:text-white">
              {((item.price || item.unit_price || 0) * item.quantity).toLocaleString()} F
            </p>
            
            {!isSent && (
              <div className="flex items-center gap-1">
                {/* Supplement button "+" */}
                {onOpenSupplementDialog && (
                  <button 
                    onClick={() => onOpenSupplementDialog(index)}
                    className={cn(
                      "w-11 h-11 rounded-xl flex items-center justify-center",
                      "bg-amber-100 border-2 border-amber-300 shadow-sm",
                      "active:scale-95 active:bg-amber-200",
                      "dark:bg-amber-900/30 dark:border-amber-700 dark:active:bg-amber-900/50",
                      "touch-manipulation"
                    )}
                    title="Ajouter un supplément"
                  >
                    <Plus className="w-5 h-5 text-amber-700 dark:text-amber-400" strokeWidth={3} />
                  </button>
                )}
                
                {/* Decrease button */}
                <button 
                  onClick={() => onUpdateQuantity(index, -1)}
                  className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center",
                    "bg-white border-2 border-gray-300 shadow-sm",
                    "active:scale-95 active:bg-gray-100",
                    "dark:bg-zinc-700 dark:border-zinc-600 dark:active:bg-zinc-600",
                    "touch-manipulation"
                  )}
                >
                  <Minus className="w-5 h-5 text-gray-700 dark:text-gray-200" strokeWidth={3} />
                </button>
                
                {/* Quantity */}
                <span className="w-10 text-center font-bold text-lg text-gray-900 dark:text-white">
                  {item.quantity}
                </span>
                
                {/* Increase button */}
                <button 
                  onClick={() => onUpdateQuantity(index, 1)}
                  className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center",
                    "bg-white border-2 border-gray-300 shadow-sm",
                    "active:scale-95 active:bg-gray-100",
                    "dark:bg-zinc-700 dark:border-zinc-600 dark:active:bg-zinc-600",
                    "touch-manipulation"
                  )}
                >
                  <Plus className="w-5 h-5 text-gray-700 dark:text-gray-200" strokeWidth={3} />
                </button>
                
                {/* Remove button */}
                <button 
                  onClick={() => onRemoveItem(index)}
                  className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center",
                    "text-red-500 active:scale-95 active:bg-red-50",
                    "dark:text-red-400 dark:active:bg-red-900/20",
                    "touch-manipulation"
                  )}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // ==================== TABLE SELECTOR ====================
  const TableSelector = () => (
    <div className="relative" ref={dropdownRef}>
      {selectedTable ? (
        <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl border-2 border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
              <span className="font-bold text-white">{selectedTable.number}</span>
            </div>
            <div>
              <p className="font-bold text-emerald-800 dark:text-emerald-200">Table {selectedTable.number}</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                {selectedTable.capacity || 4} places • {selectedTable.section || 'Salle'}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            onClick={onUnlinkTable}
          >
            Délier
          </Button>
        </div>
      ) : (
        <>
          <Button 
            variant="outline"
            className={cn(
              "w-full h-12 justify-between text-base font-medium",
              "bg-white border-2 border-gray-300 text-gray-900",
              "hover:bg-gray-50 hover:border-gray-400",
              "dark:bg-zinc-800 dark:border-zinc-600 dark:text-white dark:hover:bg-zinc-700"
            )}
            onClick={() => {
              setShowTableDropdown(!showTableDropdown);
              if (!showTableDropdown && onFetchTables) {
                onFetchTables();
              }
            }}
          >
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Attribuer à une table
            </span>
            <ChevronDown className={cn("w-5 h-5 transition-transform", showTableDropdown && "rotate-180")} />
          </Button>
          
          {/* Dropdown */}
          {showTableDropdown && (
            <div className={cn(
              "absolute top-full left-0 right-0 mt-2 z-[100]",
              "bg-white dark:bg-zinc-800 border-2 border-gray-200 dark:border-zinc-700",
              "rounded-xl shadow-2xl max-h-64 overflow-y-auto"
            )}>
              {loadingTables ? (
                <div className="p-6 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Chargement...</p>
                </div>
              ) : availableTables.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucune table disponible</p>
                </div>
              ) : (
                <>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Tables libres ({availableTables.length})
                    </p>
                  </div>
                  {availableTables.map((table) => (
                    <button
                      key={table.id || table._id}
                      className={cn(
                        "w-full px-4 py-3 flex items-center justify-between",
                        "hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
                        "border-b last:border-b-0 border-gray-100 dark:border-zinc-700",
                        "touch-manipulation"
                      )}
                      onClick={() => {
                        onAssignTable(table);
                        setShowTableDropdown(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                          <span className="font-bold text-emerald-700 dark:text-emerald-300">{table.number}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-white">Table {table.number}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {table.capacity || 4} places • {table.section || 'Salle'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-emerald-600 border-emerald-300 dark:border-emerald-700">
                        Libre
                      </Badge>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
  
  // ==================== ACTION BUTTONS ====================
  const ActionButtons = ({ compact = false }) => (
    <div className="space-y-3">
      {/* Takeaway option */}
      {itemsToSend.length > 0 && (
        <label className={cn(
          "flex items-center justify-center gap-3 p-3 rounded-xl cursor-pointer",
          "bg-orange-50 border-2 border-orange-200",
          "dark:bg-orange-900/20 dark:border-orange-800",
          isTakeaway && "bg-orange-100 border-orange-400 dark:bg-orange-900/40 dark:border-orange-600"
        )}>
          <input
            type="checkbox"
            checked={isTakeaway}
            onChange={(e) => setIsTakeaway?.(e.target.checked)}
            className="w-5 h-5 rounded border-2 border-orange-400 text-orange-500 focus:ring-orange-500"
          />
          <span className={cn(
            "font-bold flex items-center gap-2",
            isTakeaway ? "text-orange-700 dark:text-orange-300" : "text-gray-600 dark:text-gray-400"
          )}>
            <Package className="w-5 h-5" />
            À emporter
          </span>
        </label>
      )}
      
      {/* Loyalty buttons */}
      {canScanLoyalty && !compact && (
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="h-12 border-2 border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
            onClick={onLoyaltyClick}
          >
            <Gift className="w-5 h-5 mr-2" />
            Fidélité
          </Button>
          <Button
            variant="outline"
            className="h-12 border-2 border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400"
            onClick={onRewardScanClick}
          >
            <ScanLine className="w-5 h-5 mr-2" />
            Récompense
          </Button>
        </div>
      )}
      
      {/* Message buttons */}
      {!compact && (
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-11 border-2 border-orange-200 text-orange-600 hover:bg-orange-50 dark:border-orange-800 dark:text-orange-400"
            onClick={() => onMessageClick?.('cuisine')}
          >
            <MessageSquare className="w-4 h-4 mr-1" />
            Msg Cuisine
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-11 border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-400"
            onClick={() => onMessageClick?.('bar')}
          >
            <Wine className="w-4 h-4 mr-1" />
            Msg Bar
          </Button>
        </div>
      )}
      
      {/* Main action buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          className={cn(
            "h-14 text-lg font-bold shadow-lg",
            canSendToKitchen 
              ? "bg-orange-500 hover:bg-orange-600 text-white" 
              : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-700 dark:text-zinc-500"
          )}
          onClick={onSendToKitchen}
          disabled={!canSendToKitchen}
        >
          <ChefHat className="w-6 h-6 mr-2" />
          {itemsToSend.length > 0 ? `Envoyer (${itemsToSend.length})` : 'Envoyé'}
        </Button>
        <Button
          className="h-14 text-lg font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg"
          onClick={onRequestPayment}
        >
          <CreditCard className="w-6 h-6 mr-2" />
          Payer
        </Button>
      </div>
    </div>
  );
  
  // ==================== MOBILE VIEW (< md) ====================
  const MobileView = () => (
    <>
      {/* Collapsed bar */}
      {!isExpanded && (
        <div 
          className={cn(
            "md:hidden fixed bottom-0 left-0 right-0 z-40",
            "bg-white dark:bg-zinc-900 border-t-2 border-gray-200 dark:border-zinc-700",
            "rounded-t-2xl shadow-2xl p-3 cursor-pointer",
            selectedTable && "bg-emerald-50 dark:bg-emerald-900/30"
          )}
          onClick={onToggleExpand}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                itemCount > 0 ? "bg-emerald-500" : "bg-gray-200 dark:bg-zinc-700"
              )}>
                <ShoppingCart className={cn("w-6 h-6", itemCount > 0 ? "text-white" : "text-gray-500")} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-lg text-gray-900 dark:text-white">
                    {itemCount > 0 ? `${itemCount} article${itemCount > 1 ? 's' : ''}` : 'Panier vide'}
                  </span>
                  {selectedTable && (
                    <Badge className="bg-emerald-500 text-white text-xs">T{selectedTable.number}</Badge>
                  )}
                </div>
                <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {totalAmount.toLocaleString()} F
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {cart.length > 0 && (
                <Button 
                  size="sm"
                  className="h-10 px-4 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                  onClick={(e) => { e.stopPropagation(); onSendToKitchen(); }}
                  disabled={!canSendToKitchen}
                >
                  <ChefHat className="w-4 h-4 mr-1" />
                  Env.
                </Button>
              )}
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-zinc-700 flex items-center justify-center">
                <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Expanded full-screen panel */}
      {isExpanded && (
        <div className="md:hidden fixed inset-0 z-50 bg-white dark:bg-zinc-900 flex flex-col">
          {/* Close handle */}
          <div 
            className="flex-shrink-0 flex flex-col items-center py-4 bg-gray-100 dark:bg-zinc-800 cursor-pointer"
            onClick={onToggleExpand}
          >
            <div className="w-20 h-2 rounded-full bg-gray-400 dark:bg-zinc-600 mb-1" />
            <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">Appuyez pour fermer</span>
          </div>
          
          {/* Header */}
          <div className={cn(
            "flex-shrink-0 p-4 border-b-2 flex items-center justify-between",
            "border-gray-200 dark:border-zinc-700",
            selectedTable && "bg-emerald-50 dark:bg-emerald-900/20"
          )}>
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                itemCount > 0 ? "bg-emerald-500" : "bg-gray-200 dark:bg-zinc-700"
              )}>
                <ShoppingCart className={cn("w-6 h-6", itemCount > 0 ? "text-white" : "text-gray-500")} />
              </div>
              <div>
                <span className="font-bold text-xl text-gray-900 dark:text-white">
                  {itemCount} article{itemCount > 1 ? 's' : ''}
                </span>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {totalAmount.toLocaleString()} F
                </p>
              </div>
            </div>
            <button
              onClick={onToggleExpand}
              className="w-12 h-12 rounded-full bg-gray-200 dark:bg-zinc-700 flex items-center justify-center"
            >
              <ChevronDown className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
          
          {/* Table selector */}
          <div className="flex-shrink-0 p-3 border-b border-gray-200 dark:border-zinc-700">
            <TableSelector />
          </div>
          
          {/* Scrollable items list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {/* Sent items */}
            {orderItems.length > 0 && (
              <>
                <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/40 rounded-xl border-2 border-green-300 dark:border-green-800">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span className="font-bold text-green-700 dark:text-green-300">
                    Articles envoyés ({orderItems.length})
                  </span>
                  <span className="ml-auto font-bold text-green-700 dark:text-green-300">
                    {orderTotals?.sentItemsTotal?.toLocaleString() || 0} F
                  </span>
                </div>
                {orderItems.map((item, index) => (
                  <CartItem key={`sent-${item.id}-${index}`} item={item} index={index} isSent={true} />
                ))}
              </>
            )}
            
            {/* New items */}
            {cart.length > 0 && (
              <>
                <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/40 rounded-xl border-2 border-blue-300 dark:border-blue-800 mt-2">
                  <ShoppingCart className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-bold text-blue-700 dark:text-blue-300">
                    À envoyer ({cart.length})
                  </span>
                  <span className="ml-auto font-bold text-blue-700 dark:text-blue-300">
                    {cartTotal.toLocaleString()} F
                  </span>
                </div>
                {cart.map((item, index) => (
                  <CartItem key={`cart-${item.product_id}-${index}`} item={item} index={index} isSent={false} />
                ))}
              </>
            )}
            
            {/* Empty state */}
            {cart.length === 0 && orderItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ShoppingCart className="w-20 h-20 text-gray-200 dark:text-zinc-700 mb-4" />
                <p className="text-lg font-medium text-gray-500 dark:text-gray-400">Panier vide</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">Cliquez sur un plat pour l'ajouter</p>
              </div>
            )}
          </div>
          
          {/* Bottom actions - collapsible */}
          <div className={cn(
            "flex-shrink-0 border-t-2 border-gray-200 dark:border-zinc-700",
            "bg-white dark:bg-zinc-900 shadow-2xl"
          )}>
            {!actionsExpanded ? (
              <div className="p-3 space-y-2">
                {/* Show takeaway option when no table is selected and there are items to send */}
                {!selectedTable && itemsToSend.length > 0 && (
                  <label className={cn(
                    "flex items-center justify-center gap-3 p-2.5 rounded-xl cursor-pointer",
                    "border-2",
                    isTakeaway 
                      ? "bg-orange-100 border-orange-400 dark:bg-orange-900/40 dark:border-orange-600" 
                      : "bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800"
                  )}>
                    <input
                      type="checkbox"
                      checked={isTakeaway}
                      onChange={(e) => setIsTakeaway?.(e.target.checked)}
                      className="w-5 h-5 rounded border-2 border-orange-400 text-orange-500 focus:ring-orange-500"
                    />
                    <span className={cn(
                      "font-bold flex items-center gap-2",
                      isTakeaway ? "text-orange-700 dark:text-orange-300" : "text-gray-600 dark:text-gray-400"
                    )}>
                      <Package className="w-5 h-5" />
                      À emporter
                    </span>
                  </label>
                )}
                
                <div className="flex justify-between items-center text-xl font-black">
                  <span className="text-gray-900 dark:text-white">TOTAL</span>
                  <span className="text-emerald-600 dark:text-emerald-400">{totalAmount.toLocaleString()} F</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    className={cn(
                      "h-12 text-base font-bold",
                      canSendToKitchen ? "bg-orange-500 hover:bg-orange-600" : "bg-gray-200 text-gray-500"
                    )}
                    onClick={onSendToKitchen}
                    disabled={!canSendToKitchen}
                  >
                    <ChefHat className="w-5 h-5 mr-2" />
                    Envoyer
                  </Button>
                  <Button
                    className="h-12 text-base font-bold bg-emerald-600 hover:bg-emerald-700"
                    onClick={onRequestPayment}
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payer
                  </Button>
                </div>
                <button 
                  className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1"
                  onClick={() => setActionsExpanded(true)}
                >
                  <ChevronUp className="w-4 h-4" />
                  Plus d'options
                </button>
              </div>
            ) : (
              <div className="p-3 space-y-3 max-h-[50vh] overflow-y-auto">
                <ActionButtons />
                <button 
                  className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1"
                  onClick={() => setActionsExpanded(false)}
                >
                  <ChevronDown className="w-4 h-4" />
                  Réduire
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
  
  // ==================== DESKTOP/TABLET VIEW (>= md) ====================
  // REMOVED: On desktop/tablet, use the inline cart in POSPageV2.jsx instead
  // The UnifiedCart component is only used for mobile
  const DesktopView = () => null;
  
  // ==================== RENDER ====================
  return (
    <>
      <MobileView />
      {/* DesktopView is now disabled - desktop uses inline cart in POSPageV2 */}
    </>
  );
};

export default UnifiedCart;
